import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Home, Building2, Gamepad2, MessagesSquare, FileBarChart,
  LogOut, Menu, Send, IndianRupee, UserCog, Activity, Users, ShieldCheck, Receipt,
  Sparkles, TimerReset, Inbox, Ticket, Percent, MapPin, Pin, TrendingUp, ShieldAlert,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

const ADMIN_NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: Home, testid: "admin-nav-dashboard" },
  { to: "/admin/vendors", label: "Vendors", icon: Building2, testid: "admin-nav-vendors" },
  { to: "/admin/intake-leads", label: "Guided Setup Leads", icon: Sparkles, testid: "admin-nav-intake-leads" },
  { to: "/admin/campaigns", label: "Campaigns", icon: Gamepad2, testid: "admin-nav-campaigns" },
  { to: "/admin/popular-stores", label: "Popular Stores", icon: Pin, testid: "admin-nav-popular-stores" },
  { to: "/admin/customers", label: "Customers", icon: Users, testid: "admin-nav-customers" },
  { to: "/admin/invoices", label: "Invoices", icon: Receipt, testid: "admin-nav-invoices" },
  { to: "/admin/support", label: "Support", icon: MessagesSquare, testid: "admin-nav-support" },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox, testid: "admin-nav-enquiries" },
  { to: "/admin/retargeting", label: "Retargeting", icon: Send, testid: "admin-nav-retargeting" },
  { to: "/admin/reports", label: "Reports", icon: FileBarChart, testid: "admin-nav-reports" },
  { to: "/admin/scheduler", label: "Scheduler Controls", icon: TimerReset, testid: "admin-nav-scheduler" },
];

const SA_EXTRA = [
  { to: "/superadmin/admins", label: "Admins", icon: UserCog, testid: "sa-nav-admins" },
  { to: "/superadmin/pricing", label: "Pricing", icon: IndianRupee, testid: "sa-nav-pricing" },
  { to: "/superadmin/analytics", label: "Revenue", icon: Activity, testid: "sa-nav-revenue" },
  { to: "/superadmin/scheduler", label: "Scheduler Controls", icon: TimerReset, testid: "sa-nav-scheduler" },
];

export default function AdminLayout({ superadmin = false, franchise = false }) {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const initials = (admin?.name || "A")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const onLogout = () => {
    logout();
    navigate(superadmin ? "/superadmin/login" : "/admin/login");
  };

  // Franchise nav — narrower scoped surface. Sub-admins see the same
  // items as the owner minus what's blocked server-side (they can
  // load Homepage editor but the page shows the "owner only" message).
  const FRANCHISE_NAV = [
    { to: "/franchise/overview", label: "Overview", icon: Home, testid: "fr-nav-overview" },
    { to: "/franchise/vendors", label: "Vendors", icon: Building2, testid: "fr-nav-vendors" },
    { to: "/franchise/campaigns", label: "Campaigns", icon: Gamepad2, testid: "fr-nav-campaigns" },
    { to: "/franchise/enquiries", label: "Enquiries", icon: Inbox, testid: "fr-nav-enquiries" },
    { to: "/franchise/leads", label: "Leads", icon: Sparkles, testid: "fr-nav-leads" },
    { to: "/franchise/homepage-editor", label: "City Homepage", icon: Pin, testid: "fr-nav-homepage" },
    { to: "/franchise/billing-coupons", label: "Billing Coupons", icon: Percent, testid: "fr-nav-billing-coupons" },
    { to: "/franchise/revenue", label: "Revenue", icon: TrendingUp, testid: "fr-nav-revenue" },
    { to: "/franchise/staff", label: "Sub-admins", icon: UserCog, testid: "fr-nav-staff" },
    { to: "/franchise/activity", label: "Activity", icon: FileBarChart, testid: "fr-nav-activity" },
  ];

  const nav = franchise
    ? FRANCHISE_NAV
    : superadmin
    ? [
        { to: "/superadmin/dashboard", label: "Dashboard", icon: Home, testid: "sa-nav-dashboard" },
        { to: "/superadmin/vendors", label: "Vendors", icon: Building2, testid: "sa-nav-vendors" },
        { to: "/superadmin/intake-leads", label: "Guided Setup Leads", icon: Sparkles, testid: "sa-nav-intake-leads" },
        { to: "/superadmin/campaigns", label: "Campaigns", icon: Gamepad2, testid: "sa-nav-campaigns" },
        { to: "/superadmin/popular-stores", label: "Popular Stores", icon: Pin, testid: "sa-nav-popular-stores" },
        { to: "/superadmin/customers", label: "Customers", icon: Users, testid: "sa-nav-customers" },
        { to: "/superadmin/invoices", label: "Invoices", icon: Receipt, testid: "sa-nav-invoices" },
        { to: "/superadmin/enquiries", label: "Enquiries", icon: Inbox, testid: "sa-nav-enquiries" },
        { to: "/superadmin/referral-codes", label: "Referral Codes", icon: Ticket, testid: "sa-nav-referral-codes" },
        { to: "/superadmin/billing-coupons", label: "Billing Coupons", icon: Percent, testid: "sa-nav-billing-coupons" },
        { to: "/superadmin/cities", label: "Cities", icon: MapPin, testid: "sa-nav-cities" },
        { to: "/superadmin/city-integrity", label: "City Integrity", icon: ShieldAlert, testid: "sa-nav-city-integrity" },
        ...SA_EXTRA,
        { to: "/superadmin/franchises", label: "Franchises", icon: UserCog, testid: "sa-nav-franchises" },
        { to: "/superadmin/banners", label: "Banner Ads", icon: FileBarChart, testid: "sa-nav-banners" },
      ]
    : ADMIN_NAV;

  const titleLabel = franchise ? `FRANCHISE · ${admin?.assigned_city || ""}` : (superadmin ? "SUPER ADMIN" : "ADMIN");

  const sidebarContent = (
    <div className="flex h-full flex-col ag-sidebar">
      <Link
        to={franchise ? "/franchise/overview" : (superadmin ? "/superadmin/dashboard" : "/admin/dashboard")}
        className="shrink-0 px-5 pt-5 pb-4 flex items-center gap-2 border-b-2 border-black/90"
      >
        <Logo size={130} light />
        <span className="sr-only">{titleLabel}</span>
      </Link>
      <nav
        className="px-3 py-4 flex-1 min-h-0 space-y-1.5 overflow-y-auto ag-sidebar-scroll"
        aria-label="Admin navigation"
        data-testid="admin-sidebar-nav"
      >
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) => `ag-nav-item ${isActive ? "ag-nav-item--active" : ""}`}
          >
            <n.icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="shrink-0 p-3 border-t-2 border-black/90 bg-white/60">
        <div className="flex items-center gap-3 px-1 py-1">
          <Avatar className="h-9 w-9 border-2 border-black">
            <AvatarFallback className="bg-[var(--graffiti-yellow)] text-black text-xs font-bold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-black truncate" data-testid="admin-name">
              {admin?.name}
            </p>
            <span
              className="mt-1 inline-flex ag-pill ag-pill--info capitalize"
              data-testid="admin-role-badge"
            >
              <ShieldCheck className="h-3 w-3" />
              {admin?.role}
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onLogout}
            aria-label="Sign out"
            data-testid="admin-logout-button"
            className="hover:bg-black hover:text-[var(--graffiti-yellow)] border-2 border-transparent hover:border-black"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="app-light min-h-screen">
      <Toaster richColors position="top-center" />
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 z-40">{sidebarContent}</aside>
      <div className={`lg:hidden fixed inset-0 z-40 ${open ? "" : "pointer-events-none"}`}>
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity ${open ? "opacity-100" : "opacity-0"}`}
          onClick={() => setOpen(false)}
        />
        <aside
          className={`absolute inset-y-0 left-0 w-72 transition-transform ${open ? "translate-x-0" : "-translate-x-full"}`}
        >
          {sidebarContent}
        </aside>
      </div>
      <header className="lg:hidden sticky top-0 z-30 ag-topbar">
        <div className="flex items-center gap-3 px-4 h-14">
          <Link to={superadmin ? "/superadmin/dashboard" : "/admin/dashboard"} aria-label="City DOOH home" className="inline-flex shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black rounded-md">
            <Logo size={104} light />
          </Link>
          <span className="sr-only">{titleLabel}</span>
          <div className="flex-1" />
          <Button variant="ghost" size="icon" onClick={() => setOpen(true)} aria-label="Open menu" className="shrink-0">
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </header>
      <main className="lg:pl-64">
        <div className="max-w-[1400px] px-4 md:px-6 lg:px-8 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
